/**
 * Cart quantity management logic
 * Handles minimum quantity constraints and quantity updates
 */

/**
 * Determines the action to take when changing quantity
 * @param {number} currentQuantity - Current item quantity
 * @param {number} change - The change amount (positive or negative)
 * @param {number} minQuantity - Minimum required quantity (defaults to 1)
 * @returns {Object} Action object with type and optional error message
 */
export const getQuantityChangeAction = (currentQuantity, change, minQuantity = 1) => {
  const current = Number(currentQuantity);
  const changeAmount = Number(change);
  const newQuantity = current + changeAmount;
  const effectiveMinQuantity = minQuantity || 1;

  // Case 1: Trying to reduce to 0 - should prompt for deletion
  if (newQuantity === 0) {
    return {
      type: 'DELETE',
      message: 'You are about to delete this item!',
    };
  }

  // Case 2: At minimum quantity and trying to decrease - prompt for deletion
  // This allows users to delete items at minimum quantity
  if (current === effectiveMinQuantity && changeAmount < 0) {
    return {
      type: 'DELETE',
      message: 'You are about to delete this item!',
    };
  }

  // Case 3: Trying to reduce below minimum (but not at minimum) - should show error
  if (newQuantity < effectiveMinQuantity) {
    return {
      type: 'ERROR',
      message: `This item requires a minimum quantity of ${effectiveMinQuantity}. Please remove the entire item if you don't want ${effectiveMinQuantity} or more.`,
    };
  }

  // Case 4: Valid quantity change
  return {
    type: 'UPDATE',
    newQuantity,
  };
};

/**
 * Checks if the decrease button should be disabled
 * Note: We want to allow deletion even at minimum quantity,
 * so the button should never be disabled
 * @param {number} currentQuantity - Current item quantity
 * @param {number} minQuantity - Minimum required quantity
 * @returns {boolean} Always returns false to allow deletion
 */
export const shouldDisableDecreaseButton = (currentQuantity, minQuantity = 1) => {
  // Button should never be disabled - users should always be able to
  // attempt to decrease (which will either delete or show an error)
  return false;
};

/**
 * Checks if an item is at its minimum quantity
 * @param {number} currentQuantity - Current item quantity
 * @param {number} minQuantity - Minimum required quantity
 * @returns {boolean} True if at minimum quantity
 */
export const isAtMinimumQuantity = (currentQuantity, minQuantity = 1) => {
  const effectiveMinQuantity = minQuantity || 1;
  return currentQuantity <= effectiveMinQuantity;
};

/**
 * Gets the minimum quantity for an item
 * @param {Object} dish - The dish object
 * @returns {number} The minimum quantity (defaults to 1)
 */
export const getMinimumQuantity = (dish) => {
  return (dish && dish.minQuantity) ? dish.minQuantity : 1;
};
